import React, { useCallback, useEffect, useState } from 'react';
import { useApi } from '../hooks/useApi';
import { formatMoney } from '../utils/money';
import { FormAlert } from './form';
import { readApiJson } from '../utils/apiResponse';

function ScenarioCard({ scenario, baseline, modoOscuro, onSelect, loading, selectedId }) {
  const textPri = modoOscuro ? 'text-white' : 'text-gray-900';
  const textMuted = modoOscuro ? 'text-gray-400' : 'text-gray-500';
  const isSelected = selectedId === scenario.id;
  const deltaTope = (scenario.porcentajeTope || 0) - (baseline.porcentajeTope || 0);
  const deltaCuota = (scenario.cuotaMensual || 0) - (baseline.cuotaMensual || 0);
  const alert = (scenario.porcentajeTope || 0) >= 80 || scenario.cambiaCategoria;

  return (
    <button
      type="button"
      onClick={() => onSelect(scenario.id)}
      disabled={loading}
      className={`rounded-xl border p-3 text-left transition-colors disabled:opacity-60 ${
        isSelected
          ? modoOscuro
            ? 'border-[#1F80FF] bg-[#1F80FF]/10 ring-1 ring-[#1F80FF]/40'
            : 'border-[#1F80FF] bg-blue-50/70 ring-1 ring-[#1F80FF]/30'
          : alert
            ? modoOscuro
              ? 'border-amber-700/60 bg-amber-950/20 hover:border-amber-600'
              : 'border-amber-200 bg-amber-50/60 hover:border-amber-300'
            : modoOscuro
              ? 'border-gray-700 hover:border-[#1F80FF] hover:bg-gray-800/60'
              : 'border-gray-200 hover:border-[#1F80FF] hover:bg-blue-50/40'
      }`}
    >
      <div className={`text-sm font-semibold ${textPri}`}>{scenario.label}</div>
      <div className={`text-[11px] mt-0.5 ${textMuted}`}>{scenario.hint}</div>
      <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
        <div>
          <div className={`text-[10px] uppercase ${textMuted}`}>Tope</div>
          <div className={`font-semibold tabular-nums ${textPri}`}>{Math.round(scenario.porcentajeTope || 0)}%</div>
          {deltaTope !== 0 && (
            <div className={`text-[10px] ${deltaTope > 0 ? 'text-amber-600' : textMuted}`}>
              {deltaTope > 0 ? '+' : ''}{Math.round(deltaTope)} pp
            </div>
          )}
        </div>
        <div>
          <div className={`text-[10px] uppercase ${textMuted}`}>Cuota</div>
          <div className={`font-semibold tabular-nums ${textPri}`}>{formatMoney(scenario.cuotaMensual, 'ARS')}</div>
          {deltaCuota !== 0 && (
            <div className={`text-[10px] ${deltaCuota > 0 ? 'text-amber-600' : textMuted}`}>
              {deltaCuota > 0 ? '+' : ''}{formatMoney(deltaCuota, 'ARS')}
            </div>
          )}
        </div>
      </div>
      {scenario.cambiaCategoria && (
        <p className={`mt-2 text-[11px] ${modoOscuro ? 'text-amber-200' : 'text-amber-800'}`}>
          Podrías pasar a cat. {scenario.categoriaProyectada}.
        </p>
      )}
      <p className={`mt-2 text-[10px] ${textMuted}`}>
        +{formatMoney(scenario.facturacionExtra, 'ARS')} proyectados
      </p>
    </button>
  );
}

export default function FiscalSimulator({ modoOscuro, regimen }) {
  const { secureFetch } = useApi();
  const [data, setData] = useState(null);
  const [extra, setExtra] = useState('');
  const [customResult, setCustomResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [simulating, setSimulating] = useState(false);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState(null);

  const textPri = modoOscuro ? 'text-white' : 'text-gray-900';
  const textMuted = modoOscuro ? 'text-gray-400' : 'text-gray-500';
  const box = modoOscuro ? 'border-gray-700 bg-gray-800/40' : 'border-gray-200 bg-gray-50';

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await secureFetch('/api/fiscal/escenarios');
      const json = await readApiJson(res);
      if (!res.ok) throw new Error(json.message || 'No se pudieron cargar los escenarios');
      setData(json);
    } catch (err) {
      setError(err.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [secureFetch]);

  useEffect(() => {
    if (regimen === 'monotributo') load();
  }, [regimen, load]);

  const simularEscenario = async (escenarioId) => {
    setSimulating(true);
    setError('');
    setSelectedId(escenarioId);
    setCustomResult(null);
    try {
      const res = await secureFetch('/api/fiscal/simular', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ escenarioId }),
      });
      const json = await readApiJson(res);
      if (!res.ok) throw new Error(json.message || 'No se pudo simular');
      setCustomResult(json.escenario || null);
    } catch (err) {
      setError(err.message);
    } finally {
      setSimulating(false);
    }
  };

  const simularPersonalizado = async (e) => {
    e.preventDefault();
    setSimulating(true);
    setError('');
    setSelectedId('personalizado');
    try {
      const res = await secureFetch('/api/fiscal/simular', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ facturacionExtra: Number(extra) || 0 }),
      });
      const json = await readApiJson(res);
      if (!res.ok) throw new Error(json.message || 'No se pudo simular');
      setCustomResult(json.escenario || null);
    } catch (err) {
      setError(err.message);
      setCustomResult(null);
    } finally {
      setSimulating(false);
    }
  };

  if (regimen !== 'monotributo') return null;

  return (
    <div id="simulador" className={`rounded-xl border p-4 space-y-4 ${box}`}>
      <div>
        <h3 className={`text-sm font-semibold ${textPri}`}>Simulador Monotributo con escenarios</h3>
        <p className={`text-xs mt-0.5 ${textMuted}`}>
          Proyectá categoría, tope y cuota según tu ritmo de facturación. Orientativo, no es liquidación oficial.
        </p>
      </div>

      {loading && <p className={`text-xs ${textMuted}`}>Cargando escenarios…</p>}
      <FormAlert type="error">{error}</FormAlert>

      {data?.baseline && (
        <div className={`rounded-lg border p-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs ${modoOscuro ? 'border-gray-700' : 'border-gray-200'}`}>
          <div>
            <div className={`text-[10px] uppercase ${textMuted}`}>Hoy · cat. {data.baseline.categoria || '—'}</div>
            <div className={`font-semibold ${textPri}`}>{Math.round(data.baseline.porcentajeTope || 0)}% del tope</div>
          </div>
          <div>
            <div className={`text-[10px] uppercase ${textMuted}`}>Facturado 12 m</div>
            <div className={`font-semibold ${textPri}`}>{formatMoney(data.baseline.facturacion12m, 'ARS')}</div>
          </div>
          <div>
            <div className={`text-[10px] uppercase ${textMuted}`}>Ritmo mensual</div>
            <div className={`font-semibold ${textPri}`}>{formatMoney(data.baseline.ritmoMensual, 'ARS')}</div>
          </div>
          <div>
            <div className={`text-[10px] uppercase ${textMuted}`}>Cuota estimada</div>
            <div className={`font-semibold ${textPri}`}>{formatMoney(data.baseline.cuotaMensual, 'ARS')}</div>
          </div>
        </div>
      )}

      {data?.escenarios?.length > 0 && (
        <div className="space-y-2">
          <div className={`text-xs font-semibold ${textPri}`}>Escenarios predefinidos</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {data.escenarios.map((scenario) => (
              <ScenarioCard
                key={scenario.id}
                scenario={scenario}
                baseline={data.baseline}
                modoOscuro={modoOscuro}
                onSelect={simularEscenario}
                loading={simulating}
                selectedId={selectedId}
              />
            ))}
          </div>
        </div>
      )}

      <form onSubmit={simularPersonalizado} className="space-y-2">
        <div className={`text-xs font-semibold ${textPri}`}>Escenario personalizado</div>
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[160px]">
            <label className={`block text-xs font-medium mb-1 ${textMuted}`}>Facturación extra (ARS)</label>
            <input
              type="number"
              min="0"
              step="1000"
              value={extra}
              onChange={(e) => setExtra(e.target.value)}
              placeholder="Ej. 500000"
              className={`w-full rounded-xl border px-3 py-2 text-sm ${modoOscuro ? 'bg-gray-900 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
            />
          </div>
          <button
            type="submit"
            disabled={simulating}
            className="px-4 py-2 rounded-xl text-sm font-semibold bg-[#1F80FF] text-white hover:bg-[#004AAD] disabled:opacity-50"
          >
            {simulating && selectedId === 'personalizado' ? 'Calculando…' : 'Simular extra'}
          </button>
        </div>
      </form>

      {customResult && (
        <div className={`rounded-lg border p-3 text-sm ${modoOscuro ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className={`text-xs font-semibold mb-2 ${textPri}`}>
            Resultado · {customResult.label}
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <div className={`text-[10px] uppercase ${textMuted}`}>Tope simulado</div>
              <div className={`font-semibold ${(customResult.porcentajeTope || 0) >= 80 ? 'text-amber-600' : textPri}`}>
                {Math.round(customResult.porcentajeTope || 0)}%
              </div>
            </div>
            <div>
              <div className={`text-[10px] uppercase ${textMuted}`}>Cuota simulada</div>
              <div className={`font-semibold ${textPri}`}>{formatMoney(customResult.cuotaMensual, 'ARS')}</div>
            </div>
          </div>
          {customResult.cambiaCategoria && (
            <p className={`text-xs mt-2 ${modoOscuro ? 'text-amber-200' : 'text-amber-800'}`}>
              Podrías pasar de categoría {customResult.categoriaActual} a {customResult.categoriaProyectada}.
            </p>
          )}
          <p className={`text-[11px] mt-2 ${textMuted}`}>
            Base 12m {formatMoney(data?.baseline?.facturacion12m, 'ARS')} + extra {formatMoney(customResult.facturacionExtra, 'ARS')}
          </p>
        </div>
      )}

      {data?.disclaimer && (
        <p className={`text-[11px] ${textMuted}`}>{data.disclaimer}</p>
      )}
    </div>
  );
}
